import { ComponentPropsWithoutRef, FC, ReactNode, Ref } from 'react';
import classNames from 'classnames';
import { RadioGroup as RadixRadioGroup } from 'radix-ui';

import RadioGroupItem from './RadioGroupItem/RadioGroupItem';
import styles from './RadioGroup.module.css';

type RadioGroupRootPassthroughProps = Omit<
  ComponentPropsWithoutRef<typeof RadixRadioGroup.Root>,
  'className' | 'value' | 'onValueChange' | 'defaultValue' | 'orientation'
>;

export type RadioGroupProps = {
  /** Текущее выбранное значение — управляется снаружи. `null`, если ни один вариант не выбран. */
  value: string | null;
  /** Вызывается при выборе варианта пользователем. */
  onValueChange: (value: string) => void;
  /** Варианты — `RadioGroup.Item`. */
  children: ReactNode;
  /** Класс для корневого элемента группы. */
  className?: string;
  /** Расположение вариантов: столбцом или в строку. По умолчанию `vertical`. */
  orientation?: 'horizontal' | 'vertical';
  /** Ref на корневой элемент группы. */
  ref?: Ref<HTMLDivElement>;
} & RadioGroupRootPassthroughProps;

/** Группа радио-кнопок на примитиве Radix RadioGroup. */
const RadioGroup: FC<RadioGroupProps> = ({
  value,
  onValueChange,
  children,
  className,
  orientation = 'vertical',
  ref,
  ...rest
}) => (
  <RadixRadioGroup.Root
    ref={ref}
    value={value}
    onValueChange={onValueChange}
    orientation={orientation}
    {...rest}
    className={classNames(styles.root, className)}>
    {children}
  </RadixRadioGroup.Root>
);

type RadioGroupCompound = typeof RadioGroup & {
  Item: typeof RadioGroupItem;
};

const CompoundRadioGroup = RadioGroup as RadioGroupCompound;
CompoundRadioGroup.Item = RadioGroupItem;

export default CompoundRadioGroup;
